import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  Divider,
  Alert,
} from '@mui/material';
import { toast } from 'react-toastify';

// Services
import jobOverviewTemplateServices from '../services/jobOverviewTemplateServices';

const JobOverviewTemplateDialog = ({ open, onClose, currentJobOverview, onApplyTemplate }) => {
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  // Load templates
  const loadTemplates = async () => {
    try {
      setLoading(true);
      const response = await jobOverviewTemplateServices.getAll();
      if (response.status === 200) {
        setTemplates(response.data);
      }
    } catch (error) {
      console.log(error);
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      loadTemplates();
      setSelectedTemplateId('');
      setSelectedTemplate(null);
      setShowConfirm(false);
    }
  }, [open]);

  // Handle template selection
  const handleSelectTemplate = (e) => {
    const templateId = e.target.value;
    setSelectedTemplateId(templateId);
    
    if (templateId) {
      const template = templates.find(t => t.id === parseInt(templateId));
      setSelectedTemplate(template);
    } else {
      setSelectedTemplate(null);
    }
  };

  // Handle apply button
  const handleApply = () => {
    if (!selectedTemplate) {
      toast.error('Please select a template');
      return;
    }

    // If there's existing content, show confirmation dialog
    if (currentJobOverview && currentJobOverview.trim()) {
      setShowConfirm(true);
    } else {
      // No existing content, just replace
      onApplyTemplate(selectedTemplate.description, 'replace');
      handleClose();
    }
  };

  // Handle replace action
  const handleReplace = () => {
    onApplyTemplate(selectedTemplate.description, 'replace');
    handleClose();
  };

  // Handle append action
  const handleAppend = () => {
    onApplyTemplate(selectedTemplate.description, 'append');
    handleClose();
  };

  // Handle close
  const handleClose = () => {
    setSelectedTemplateId('');
    setSelectedTemplate(null);
    setShowConfirm(false);
    onClose();
  };

  // Group templates by category
  const groupedTemplates = templates.reduce((acc, template) => {
    const category = template.category || 'Other';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(template);
    return acc;
  }, {});

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {showConfirm ? 'Apply Template' : 'Select Job Overview Template'}
      </DialogTitle>
      
      <DialogContent>
        {!showConfirm ? (
          <>
            <FormControl fullWidth sx={{ mt: 2, mb: 2 }}>
              <InputLabel>Select Template</InputLabel>
              <Select
                value={selectedTemplateId}
                onChange={handleSelectTemplate}
                label="Select Template"
                disabled={loading}
              >
                <MenuItem value="">
                  <em>Select a template...</em>
                </MenuItem>
                {Object.entries(groupedTemplates).map(([category, categoryTemplates]) => [
                  <MenuItem key={`cat-${category}`} disabled sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                    {category}
                  </MenuItem>,
                  ...categoryTemplates.map(template => (
                    <MenuItem key={template.id} value={template.id} sx={{ pl: 4 }}>
                      {template.name}
                    </MenuItem>
                  ))
                ])}
              </Select>
            </FormControl>

            {selectedTemplate && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Preview:
                </Typography>
                <Box 
                  sx={{ 
                    p: 2, 
                    backgroundColor: '#f9f9f9', 
                    borderRadius: 1,
                    border: '1px solid #e0e0e0',
                    maxHeight: 300,
                    overflow: 'auto'
                  }}
                >
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {selectedTemplate.description}
                  </Typography>
                </Box>
              </Box>
            )}
          </>
        ) : (
          <>
            <Alert severity="info" sx={{ mb: 2 }}>
              The Job Overview field already contains text. How would you like to apply the template?
            </Alert>
            
            <Typography variant="subtitle2" gutterBottom>
              Template: <strong>{selectedTemplate?.name}</strong>
            </Typography>
            
            <Divider sx={{ my: 2 }} />
            
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Button 
                variant="contained" 
                color="error" 
                onClick={handleReplace}
                sx={{ minWidth: 150 }}
              >
                Replace Existing
              </Button>
              <Button 
                variant="contained" 
                color="primary" 
                onClick={handleAppend}
                sx={{ minWidth: 150 }}
              >
                Append to Existing
              </Button>
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        {!showConfirm && (
          <Button 
            onClick={handleApply} 
            variant="contained" 
            disabled={!selectedTemplate}
          >
            Apply Template
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default JobOverviewTemplateDialog;

